<script lang="ts" setup>
import { FloatingPanel, Popup, showSuccessToast } from "vant";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import {
  useSwipe,
  UseSwipeDirection,
  useThrottleFn,
  useWindowSize,
} from "@vueuse/core";
import { copyToClipboard, isMobile } from "@/modules/common";
import { drawByTextImage, drawByTrait } from "@/composable/task/textImage";
import { useReply } from "@/stores/reply";
import ReactionCounter from "@/components/mobile/reply/ReactionCounter.vue";
import { usePreviewImg, useTextImageUrl } from "@/use/common";
import i18n from "@/locales";
import { TextImageModeEnum } from "@/enums/TextImageEnum";

const props = defineProps<{
  show: boolean;
  list: TextImage[];
  idx: number;
  nextPageFunc: () => Promise<boolean> | boolean;
}>();
const emit = defineEmits<{
  (e: "update:show", val: boolean): void;
  (e: "update:idx", val: number): void;
}>();

const replyStore = useReply();
const target = computed(() => props.list[props.idx]);
const itemsRef = ref<HTMLDivElement[]>([]);
const isSmallScreen = computed(() => width.value <= 1024);
const displayList = computed(() => {
  const displayMaxLength = isSmallScreen.value ? 15 : 30;
  const idx = props.idx;
  const start = Math.max(0, idx - Math.floor(displayMaxLength / 2));
  const end = Math.min(props.list.length, start + displayMaxLength);
  return props.list.slice(start, end);
});
const imgContainer = ref<HTMLDivElement | null>(null);
const thumbnailList = ref<HTMLDivElement | null>(null);

// transition to the selected image, center it
const y = ref(0);
const x = ref(0);
const { height, width } = useWindowSize();

const scrollLoading = ref(false);
const hasMore = ref(true);

// Lifecycle
const moveImage = async () => {
  await nextTick();
  const idx = displayList.value.findIndex((i) => i.id === target.value.id);
  const targetRef = itemsRef.value[idx];
  if (!targetRef) return;
  const rect = targetRef.getBoundingClientRect();
  if (isSmallScreen.value) {
    // 横向
    x.value = x.value + width.value / 2 - rect.left - targetRef.offsetWidth / 2;
  } else {
    const center = height.value / 2;
    y.value = y.value + center - rect.top - targetRef.offsetHeight / 2;
  }
};

function onSwipeEnd(e: TouchEvent, direction: UseSwipeDirection) {
  if (!isMobile()) return;
  if (direction === "left") {
    nextImage();
  } else if (direction === "right") {
    prevImage();
  } else if (direction === "down") {
    emit("update:show", false);
  }
}

onMounted(async () => {
  await moveImage();
  await checkScrollLoad();
  window.addEventListener("keydown", onKeyUp);
  useSwipe(imgContainer, {
    passive: false,
    onSwipeEnd,
  });
  useSwipe(thumbnailList, {
    onSwipeEnd,
  });
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeyUp);
});

const loadingOffset = 8;
const checkScrollLoad = async () => {
  if (scrollLoading.value || !hasMore.value) return;
  if (props.idx > props.list.length - loadingOffset) {
    scrollLoading.value = true;
    try {
      hasMore.value = await props.nextPageFunc();
    } finally {
      scrollLoading.value = false;
    }
  }
};
watch([() => props.idx, height], async ([nextIdx], [prevIdx]) => {
  await moveImage();
  const isMoveDown = nextIdx > prevIdx;
  if (
    prevIdx === nextIdx ||
    scrollLoading.value ||
    !hasMore.value ||
    !isMoveDown
  )
    return;
  await checkScrollLoad();
});

// Event handler
const prevImage = () => {
  emit("update:idx", Math.max(props.idx - 1, 0));
};
const nextImage = async () => {
  emit("update:idx", Math.min(props.idx + 1, props.list.length - 1));
};
const onKeyUp = (e: KeyboardEvent) => {
  if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
    e.preventDefault();
    prevImage();
  } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
    e.preventDefault();
    nextImage();
  } else if (e.key === "Escape") {
    emit("update:show", false);
  }
};

const isShareShow = ref(false);
const handleWheel = (e: WheelEvent) => {
  // console.log(e.deltaY);
  if (Math.abs(e.deltaY) < 2) return;
  if (e.deltaY > 0) {
    nextImage();
  } else {
    prevImage();
  }
};
const throttleHandleWheel = useThrottleFn(handleWheel, 100, true);
const shareUrl = computed(() => useTextImageUrl(target.value.uuid));
const onCopy = async () => {
  copyToClipboard(shareUrl.value);
  showSuccessToast(i18n.global.t("Message.copySuccessful"));
};

const triggerDraw = (target: TextImage) => {
  if (
    [
      TextImageModeEnum.TextGenerateFeature,
      TextImageModeEnum.ImageGenerateFeature,
      TextImageModeEnum.PresetFeature,
    ].includes(target.mode!)
  ) {
    drawByTrait(target);
  } else {
    drawByTextImage(target);
  }
};
</script>
<template>
  <div
    class="lg:grid lg:grid-cols-[1fr_auto] flex flex-col-reverse w-full h-full overflow-hidden relative"
  >
    <div
      class="w-full h-full relative overflow-hidden flex flex-col items-center flex-grow"
      @wheel="throttleHandleWheel"
    >
      <!-- <button
        class="btn btn-circle btn-neutral absolute right-5 top-20 lg:top-5 bg-opacity-80"
        @click.stop="emit('update:show', false)"
      >
        <i class="iconify mingcute--close-line" />
      </button> -->
      <div
        ref="imgContainer"
        class="full h-full flex flex-col items-center justify-center p-4 lg:p-8 overflow-hidden"
      >
        <ImageCard
          :enable-hover-scale="false"
          :is-nsfw="target.isNsfw || target.media!.isNsfw"
          :media="target.media!"
          class="rounded-lg max-h-full max-w-full shrink-0 cursor-auto"
        />
      </div>
      <div
        class="absolute top-1 lg:top-auto lg:bottom-5 p-2 rounded-full shadow-lg flex border max-w-[20.5rem] lg:max-w-full bg-base-100 justify-between px-4 gap-2"
      >
        <div
          v-if="target.reply.interactivityEmoji.length > 0"
          class="flex items-center flex-grow"
        >
          <reaction-counter :reply="target.reply" :top-n="4" ghost />
        </div>
        <div
          class="flex gap-2 justify-items-stretch items-center justify-center"
        >
          <button
            :disabled="replyStore.voteLoading"
            class="btn btn-circle btn-sm"
            @click.stop="replyStore.popEmoji(target.reply)"
          >
            <happy-face-icon v-if="!replyStore.voteLoading" />
            <span v-else class="loading loading-sm" />
          </button>
          <div :data-tip="$t('Buttons.generate')" class="tooltip tooltip-top">
            <button
              class="btn btn-circle btn-primary btn-sm"
              @click="triggerDraw(target)"
            >
              <i class="iconify mdi--paintbrush" />
            </button>
          </div>
          <button
            class="btn btn-circle btn-sm"
            @click="emit('update:show', false)"
          >
            <i class="iconify mingcute--close-line" />
          </button>
        </div>
        <!--          <button-->
        <!--            class="btn btn-circle  btn-xs lg:hidden"-->
        <!--            @click="drawByTextImage(target)"-->
        <!--          >-->
        <!--            <i class="iconify mdi&#45;&#45;information-variant" />-->
        <!--          </button>-->
      </div>
      <!--        <div-->
      <!--          class="absolute bottom-10 right-5 p-3 rounded-2xl shadow-lg flex gap-4"-->
      <!--        >-->
      <!--          <div class="flex w-full justify-center">-->
      <!--            <span class="flex items-center">-->
      <!--              <span>Prev·</span>-->
      <!--              <kbd class="kbd kbd-sm">-->
      <!--                <i class="iconify mingcute&#45;&#45;arrow-up-line"-->
      <!--              /></kbd>-->
      <!--            </span>-->
      <!--            <kbd class="kbd">▼</kbd>-->
      <!--          </div>-->
      <!--        </div>-->
    </div>
    <div
      class="h-[4.5rem] lg:w-[30rem] lg:h-full bg-overlay-blue flex justify-between overflow-x-hidden relative"
    >
      <!-- Image Info -->
      <TextImageInfo
        v-if="!isSmallScreen"
        :target="target"
        @show-share="isShareShow = true"
      />
      <!-- Thumbnail List -->
      <div
        ref="thumbnailList"
        class="flex lg:flex-col h-full items-center lg:items-end hover:larger relative lg:pr-2 lg:pl-3 lg:pb-0 lg:overflow-y-hidden pt-1 shrink-0"
        @wheel="throttleHandleWheel"
      >
        <!-- Notice: 这里的key必须用index而不是id，避免rerender导致的高度变化使transitionY计算错误 -->
        <div
          v-for="(task, idx) in displayList"
          :key="idx"
          ref="itemsRef"
          :class="{
            'relative z-10': target.id === task.id,
            'lg:opacity-50 lg:hover:opacity-100': target.id !== task.id,
          }"
          :style="`transform: ${
            isSmallScreen ? `translateX(${x}px)` : `translateY(${y}px)`
          } translateZ(0) ${
            target.id === task.id ? 'scale(1.25)' : 'scale(1)'
          }`"
          class="w-12 h-12 xl:w-20 xl:h-20 rounded-lg overflow-hidden shrink-0 cursor-pointer border-2 border-transparent transform-gpu lg:hover:border-primary bg-base-200"
          @click="
            emit(
              'update:idx',
              list.findIndex((i) => i.id === task.id)
            )
          "
        >
          <ImageCard
            :enable-animation="false"
            :enableBlurToggleBtn="false"
            :is-nsfw="task.isNsfw || task.media!.isNsfw"
            :media="task.media!"
            blur="blur"
            class="w-full h-full"
          />
        </div>
        <div
          v-for="(_range, idx) in Array(5)"
          v-show="scrollLoading"
          :key="idx"
          :style="`transform: ${
            isSmallScreen ? `translateX(${x}px)` : `translateY(${y}px)`
          } translateZ(0)`"
          class="w-12 h-12 xl:w-20 xl:h-20 rounded-lg skeleton-loading-bg shrink-0 border-2 border-transparent transform-gpu"
        />
      </div>
    </div>
    <FloatingPanel v-if="isSmallScreen" :anchors="[100, height * 0.5, height]">
      <TextImageInfo
        :target="target"
        class="shrink-0 h-auto"
        @show-share="isShareShow = true"
      />
    </FloatingPanel>
    <popup
      v-model:show="isShareShow"
      :closeable="true"
      class="p-4"
      position="center"
      round
    >
      <div class="font-bold text-base">{{ $t("share.name") }}</div>
      <div class="divider">{{ $t("share.shareWithLink") }}</div>
      <div
        class="text-base-content/50 bg-neutral rounded flex gap-2 items-center p-2"
      >
        <div class="flex">{{ useTextImageUrl(target.uuid) }}</div>
        <button class="shrink-0 btn btn-sm btn-ghost" @click="onCopy">
          <i class="iconfont icon-copy" />
        </button>
      </div>
    </popup>
  </div>
</template>
<style scoped>
.bg-overlay-blue {
  @apply bg-[rgba(13,14,18)];
}
</style>
